import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchCollection, getOptimizedImageUrl } from '../lib/cms';
import TreatmentCard from '../components/TreatmentCard';
import PriceList from '../components/PriceList';
import ContactForm from '../components/ContactForm';
import GallerySection from '../components/GallerySection';
import SEO from '../components/SEO';

const BOOKING_URL = 'https://cryobybreum.planway.com/';

const HERO_SALON_IMAGES = [
  {
    url: 'https://assets.auxodev.dk/assets/bbc8c269-a12e-46e2-947b-0cefa0025eea/cryo-by-breum-reception-og-ventevaerelse.jpg',
    alt: 'Lyst receptionsområde og venteværelse med sildebensparket og lædersofa hos Cryo by Breum i Odense'
  },
  {
    url: 'https://assets.auxodev.dk/assets/bbc8c269-a12e-46e2-947b-0cefa0025eea/cryo-by-breum-ems-studio-behandlingsrum.jpg',
    alt: 'Privat EMS træningsstudio og lyst behandlingsrum med behandlerbriks og trælameller hos Cryo by Breum'
  },
  {
    url: 'https://assets.auxodev.dk/assets/bbc8c269-a12e-46e2-947b-0cefa0025eea/cryo-by-breum-klinik-behandlingsrum-briks.jpg',
    alt: 'Harmonisk og lyst behandlingsrum med hvid behandlerbriks og professionelt udstyr hos Cryo by Breum'
  },
  {
    url: 'https://assets.auxodev.dk/assets/bbc8c269-a12e-46e2-947b-0cefa0025eea/cryo-by-breum-body-roll-massage-maskine.jpg',
    alt: 'Sort Body Roll massagemaskine med infrarød varme til lymfedrænage i kliniklokale hos Cryo by Breum'
  }
];

const INITIAL_TREATMENTS = [
  {
    id: 'fedtfrysning',
    order: 1,
    title: 'Fedtfrysning',
    slug_id: 'fedtfrysning',
    short_description: 'Kontrolleret og skånsom kuldebehandling af udvalgte fedtdepoter i rolige, professionelle rammer.',
    featured_image: [
      'https://assets.auxodev.dk/assets/bbc8c269-a12e-46e2-947b-0cefa0025eea/cryo-by-breum-fedtfrysning-behandling-applikatorer.jpg'
    ]
  },
  {
    id: 'ems-traening',
    order: 2,
    title: 'EMS Træning',
    slug_id: 'ems-traening',
    short_description: 'Effektiv og skånsom træningsform med elektrisk muskelstimulation under personlig vejledning.',
    featured_image: [
      'https://assets.auxodev.dk/assets/bbc8c269-a12e-46e2-947b-0cefa0025eea/cryo-by-breum-ems-studio-behandlingsrum.jpg'
    ]
  },
  {
    id: 'body-roll',
    order: 3,
    title: 'Body Roll',
    slug_id: 'body-roll',
    short_description: 'Dybdegående lymfedrænerende rullemassage med infrarød varme for øget cirkulation og velvære.',
    featured_image: [
      'https://assets.auxodev.dk/assets/bbc8c269-a12e-46e2-947b-0cefa0025eea/cryo-by-breum-body-roll-massage-maskine.jpg'
    ]
  }
];

export default function Home() {
  const [treatments, setTreatments] = useState(INITIAL_TREATMENTS);
  const [popularPrices, setPopularPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentHeroIndex, setCurrentHeroIndex] = useState(0);

  // Auto-cycle hero images smoothly every 5 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentHeroIndex((prevIndex) => (prevIndex + 1) % HERO_SALON_IMAGES.length);
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    document.title = 'Cryo by Breum';
    let mounted = true;

    async function loadData() {
      const [treatmentData, priceData] = await Promise.all([
        fetchCollection('treatments'),
        fetchCollection('price_items'),
      ]);

      if (mounted) {
        if (treatmentData && treatmentData.length > 0) {
          setTreatments(treatmentData.sort((a, b) => (a.order || 0) - (b.order || 0)));
        }
        // Select key featured/popular prices
        const featured = priceData.filter(p => p.is_offer || p.order <= 4);
        setPopularPrices(featured.length > 0 ? featured : priceData.slice(0, 4));
        setLoading(false);
      }
    }

    loadData();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="bg-white">
      <SEO preset="home" />
      {/* 1. HERO SECTION WITH TEXT OVERLAID ON FULL-WIDTH IMAGE SLIDESHOW */}
      <section className="relative min-h-[660px] sm:min-h-[760px] lg:min-h-[860px] flex items-center justify-center overflow-hidden border-b border-taupe-border/80">
        {/* Full-width Cycling Background Salon Images */}
        <div className="absolute inset-0 z-0">
          {HERO_SALON_IMAGES.map((image, index) => {
            const isActive = index === currentHeroIndex;
            return (
              <div
                key={image.url}
                className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                  isActive ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
              >
                <img
                  src={getOptimizedImageUrl(image.url, { width: 1920, quality: 90 })}
                  alt={image.alt}
                  className={`w-full h-full object-cover object-center transform transition-transform duration-7000 ease-out ${
                    isActive ? 'scale-105' : 'scale-100'
                  }`}
                  loading={index === 0 ? 'eager' : 'lazy'}
                />
              </div>
            );
          })}
          {/* Subtle darker warm scrim for high-contrast, premium look across all images */}
          <div className="absolute inset-0 bg-espresso/65 sm:bg-espresso/60 z-1" />
        </div>

        {/* Hero Content overlaid on top of image - Centered */}
        <div className="relative z-10 w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-36 text-center">
          <div className="max-w-3xl mx-auto space-y-6 sm:space-y-8 flex flex-col items-center">
            <h1 className="font-sans text-3xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl font-bold text-white tracking-widest uppercase leading-tight text-center">
              Din krop. Din tid.<br />
              <span>Dit velvære.</span>
            </h1>

            <p className="text-base sm:text-lg lg:text-xl text-sand/90 max-w-xl mx-auto leading-relaxed text-center font-light">
              Eksklusive kropsbehandlinger og målrettet træning i rolige rammer i Odense.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2 w-full sm:w-auto">
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-espresso text-xs font-semibold tracking-widest uppercase hover:bg-cream transition-all shadow-sm cursor-pointer"
              >
                <span>Book tid</span>
                <i className="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true" />
              </a>
            </div>

            {/* Quick perks */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 pt-8 border-t border-white/20 max-w-xl mx-auto w-full text-center">
              <Link
                to="/behandlinger/fedtfrysning"
                className="group block p-3 rounded-xs bg-white/5 hover:bg-white/15 border border-white/10 hover:border-white/30 transition-all text-center"
              >
                <div className="font-sans text-sm sm:text-base font-bold text-white uppercase tracking-wider group-hover:text-sand-light transition-colors">
                  Fedtfrysning
                </div>
                <div className="text-xs text-sand/80 mt-1 flex items-center justify-center gap-1">
                  <span>Kontrolleret kulde</span>
                  <i className="fa-solid fa-arrow-right text-[9px] opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" aria-hidden="true" />
                </div>
              </Link>
              <Link
                to="/behandlinger/ems-traening"
                className="group block p-3 rounded-xs bg-white/5 hover:bg-white/15 border border-white/10 hover:border-white/30 transition-all text-center"
              >
                <div className="font-sans text-sm sm:text-base font-bold text-white uppercase tracking-wider group-hover:text-sand-light transition-colors">
                  EMS Træning
                </div>
                <div className="text-xs text-sand/80 mt-1 flex items-center justify-center gap-1">
                  <span>25 min. helkrop</span>
                  <i className="fa-solid fa-arrow-right text-[9px] opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" aria-hidden="true" />
                </div>
              </Link>
              <Link
                to="/behandlinger/body-roll"
                className="group block p-3 rounded-xs bg-white/5 hover:bg-white/15 border border-white/10 hover:border-white/30 transition-all text-center"
              >
                <div className="font-sans text-sm sm:text-base font-bold text-white uppercase tracking-wider group-hover:text-sand-light transition-colors">
                  Body Roll
                </div>
                <div className="text-xs text-sand/80 mt-1 flex items-center justify-center gap-1">
                  <span>Lymfedrænage</span>
                  <i className="fa-solid fa-arrow-right text-[9px] opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" aria-hidden="true" />
                </div>
              </Link>
            </div>

            {/* Salon Image Indicators */}
            <div className="flex items-center justify-center gap-2 pt-4">
              {HERO_SALON_IMAGES.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setCurrentHeroIndex(idx)}
                  className={`h-0.5 transition-all duration-300 cursor-pointer ${
                    idx === currentHeroIndex ? 'w-8 bg-white' : 'w-4 bg-white/40 hover:bg-white/70'
                  }`}
                  aria-label={`Skift til salonbillede ${idx + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 2. INTRODUKTION */}
      <section className="py-20 bg-cream border-y border-taupe-border/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start text-left reveal-on-scroll">
            <div className="lg:col-span-5">
              <h2 className="font-sans text-2xl sm:text-3xl lg:text-4xl font-semibold text-espresso uppercase tracking-wider leading-tight">
                Et roligt, personligt og professionelt frirum i Odense
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-6 text-left">
              <p className="text-base sm:text-lg text-taupe leading-relaxed">
                Velkommen til Cryo by Breum på Dalumvej 82 i Odense SV. Vi skaber en rolig og personlig oase for dig, der ønsker skånsomme, målrettede kropsbehandlinger og effektiv træning.
              </p>
              <p className="text-base sm:text-lg text-taupe leading-relaxed">
                Uanset om du søger{' '}
                <Link to="/behandlinger/fedtfrysning" className="text-espresso font-medium hover:text-accent-brown underline underline-offset-4 decoration-accent-brown/40 hover:decoration-accent-brown transition-colors">
                  kontrolleret fedtfrysning
                </Link>
                ,{' '}
                <Link to="/behandlinger/ems-traening" className="text-espresso font-medium hover:text-accent-brown underline underline-offset-4 decoration-accent-brown/40 hover:decoration-accent-brown transition-colors">
                  tidsoptimeret EMS-træning
                </Link>{' '}
                eller dybdegående lymfedrænage med{' '}
                <Link to="/behandlinger/body-roll" className="text-espresso font-medium hover:text-accent-brown underline underline-offset-4 decoration-accent-brown/40 hover:decoration-accent-brown transition-colors">
                  Body Roll
                </Link>
                , mødes du altid af professionel rådgivning og individuelle forløb tilpasset netop din krop.
              </p>
              <div className="pt-2">
                <Link
                  to="/om-cryo-by-breum"
                  className="inline-flex items-center gap-2 px-6 py-3.5 bg-espresso hover:bg-espresso-light text-white text-xs font-semibold uppercase tracking-widest transition-colors cursor-pointer"
                >
                  <span>Læs mere om os</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. BEHANDLINGER SEKTION */}
      <section id="behandlinger" className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6 reveal-on-scroll">
          <div className="max-w-2xl">
            <h2 className="font-sans text-2xl sm:text-3xl lg:text-4xl font-semibold text-espresso uppercase tracking-wider">
              Skræddersyede behandlinger og træning
            </h2>
            <p className="text-taupe text-sm sm:text-base mt-3 leading-relaxed">
              Vælg mellem vores tre kerneydelser – udviklet til at give dig velvære, kropslig balance og konkrete resultater.
            </p>
          </div>

          <Link
            to="/priser"
            className="inline-flex items-center gap-2 text-xs font-semibold tracking-wider uppercase text-espresso hover:text-accent-brown transition-colors cursor-pointer group shrink-0"
          >
            <span>Se alle priser & forløb</span>
            <i className="fa-solid fa-arrow-right text-xs transition-transform group-hover:translate-x-1" aria-hidden="true" />
          </Link>
        </div>

        {/* Treatment Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {treatments.map((treatment, idx) => (
            <div 
              key={treatment.id || treatment.slug_id} 
              className={`reveal-on-scroll ${idx === 1 ? 'delay-150' : idx === 2 ? 'delay-300' : ''}`}
            >
              <TreatmentCard treatment={treatment} />
            </div>
          ))}
        </div>
      </section>

      {/* 4. HVORFOR CRYO BY BREUM (TROVÆRDIGHED) */}
      <section className="py-24 bg-[#fcfaf7] border-y border-taupe-border/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16 reveal-on-scroll">
            <h2 className="font-sans text-2xl sm:text-3xl lg:text-4xl font-semibold text-espresso uppercase tracking-wider">
              Fokus på kvalitet, tryghed og nærvær
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 border border-taupe-border/80 flex flex-col justify-between shadow-xs reveal-on-scroll">
              <div>
                <div className="text-espresso text-2xl mb-4">
                  <i className="fa-solid fa-user-check" aria-hidden="true" />
                </div>
                <h3 className="font-sans text-lg font-semibold text-espresso uppercase tracking-wider mb-3">
                  Personlig vejledning
                </h3>
                <p className="text-sm text-taupe leading-relaxed">
                  Hos Cryo by Breum er du i centrum. Vi starter altid med en grundig dialog og tilpasser behandlingen nøje efter dine ønsker og behov.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 border border-taupe-border/80 flex flex-col justify-between md:translate-y-6 shadow-xs reveal-on-scroll delay-150">
              <div>
                <div className="text-espresso text-2xl mb-4">
                  <i className="fa-solid fa-sliders" aria-hidden="true" />
                </div>
                <h3 className="font-sans text-lg font-semibold text-espresso uppercase tracking-wider mb-3">
                  Individuelle forløb
                </h3>
                <p className="text-sm text-taupe leading-relaxed">
                  Ingen kroppe er ens. Vores forløb tilrettelægges fleksibelt med mulighed for klippekort, prøvesessioner og målrettede kombinationer.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 border border-taupe-border/80 flex flex-col justify-between shadow-xs reveal-on-scroll delay-300">
              <div>
                <div className="text-espresso text-2xl mb-4">
                  <i className="fa-solid fa-sparkles" aria-hidden="true" />
                </div>
                <h3 className="font-sans text-lg font-semibold text-espresso uppercase tracking-wider mb-3">
                  Moderne behandlingsteknologi
                </h3>
                <p className="text-sm text-taupe leading-relaxed">
                  Vi benytter moderne og kontrolleret teknologi inden for både{' '}
                  <Link to="/behandlinger/fedtfrysning" className="text-espresso font-medium hover:text-accent-brown underline underline-offset-4 decoration-accent-brown/40 hover:decoration-accent-brown transition-colors">
                    kuldebehandling og fedtfrysning
                  </Link>
                  ,{' '}
                  <Link to="/behandlinger/ems-traening" className="text-espresso font-medium hover:text-accent-brown underline underline-offset-4 decoration-accent-brown/40 hover:decoration-accent-brown transition-colors">
                    trådløs EMS-muskelstimulation
                  </Link>{' '}
                  og{' '}
                  <Link to="/behandlinger/body-roll" className="text-espresso font-medium hover:text-accent-brown underline underline-offset-4 decoration-accent-brown/40 hover:decoration-accent-brown transition-colors">
                    infrarød Body Roll
                  </Link>
                  .
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. POPULÆRE BEHANDLINGER & PRISER FRA PLANWAY */}
      <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-6 reveal-on-scroll">
          <div>
            <h2 className="font-sans text-2xl sm:text-3xl lg:text-4xl font-semibold text-espresso uppercase tracking-wider">
              Udvalgte behandlinger
            </h2>
            <p className="text-taupe text-sm sm:text-base mt-2">
              Priserne følger klinikkens officielle Planway booking.
            </p>
          </div>

          <Link
            to="/priser"
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-espresso hover:text-accent-brown transition-colors group cursor-pointer pb-1 border-b border-espresso/30 hover:border-espresso shrink-0"
          >
            <span>Se komplet prisoversigt</span>
            <i className="fa-solid fa-arrow-right text-xs transition-transform group-hover:translate-x-1" aria-hidden="true" />
          </Link>
        </div>

        <div className="reveal-on-scroll delay-150">
          <PriceList items={popularPrices} />
        </div>
      </section>

      {/* 6. STEMNINGSGALLERI FRA CMS */}
      <GallerySection 
        gallerySlug="klinikken" 
        title="Stemningen i klinikken" 
        subtitle="Rolige omgivelser og moderne faciliteter på Dalumvej 82 i Odense SV" 
      />

      {/* 7. VISUEL BOOKING CTA SEKTION */}
      <section className="py-20 bg-accent-brown text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6 reveal-on-scroll">
          <h2 className="font-sans text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-wider uppercase text-cream">
            Klar til at booke din næste behandling?
          </h2>
          <p className="text-sand/90 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            Find en tid der passer dig direkte i vores online kalender. Vi glæder os til at byde dig velkommen hos Cryo by Breum.
          </p>
          <div className="pt-4">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-9 py-4 bg-cream text-espresso text-xs font-semibold tracking-widest uppercase hover:bg-white transition-all shadow-md cursor-pointer"
            >
              <span>Book tid hos Cryo by Breum</span>
              <i className="fa-solid fa-arrow-up-right-from-square text-xs text-taupe" aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>

      {/* 8. KONTAKT & BELIGGENHED SEKTION */}
      <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Contact Details */}
          <div className="lg:col-span-5 space-y-8 reveal-on-scroll">
            <div>
              <h2 className="font-sans text-2xl sm:text-3xl lg:text-4xl font-semibold text-espresso uppercase tracking-wider">
                Besøg klinikken i Odense
              </h2>
              <p className="text-taupe text-sm sm:text-base mt-3 leading-relaxed">
                Cryo by Breum er centralt beliggende i Dalum med gode adgangsforhold og rolige behandlingsrum til{' '}
                <Link to="/behandlinger/fedtfrysning" className="text-espresso font-medium hover:text-accent-brown underline underline-offset-4 decoration-accent-brown/40 hover:decoration-accent-brown transition-colors">
                  fedtfrysning
                </Link>
                ,{' '}
                <Link to="/behandlinger/ems-traening" className="text-espresso font-medium hover:text-accent-brown underline underline-offset-4 decoration-accent-brown/40 hover:decoration-accent-brown transition-colors">
                  EMS træning
                </Link>{' '}
                og{' '}
                <Link to="/behandlinger/body-roll" className="text-espresso font-medium hover:text-accent-brown underline underline-offset-4 decoration-accent-brown/40 hover:decoration-accent-brown transition-colors">
                  Body Roll
                </Link>
                . Se også vores{' '}
                <Link to="/kontakt" className="text-espresso font-medium hover:text-accent-brown underline underline-offset-4 decoration-accent-brown/40 hover:decoration-accent-brown transition-colors">
                  kontaktside
                </Link>{' '}
                eller læs mere{' '}
                <Link to="/om-cryo-by-breum" className="text-espresso font-medium hover:text-accent-brown underline underline-offset-4 decoration-accent-brown/40 hover:decoration-accent-brown transition-colors">
                  om os
                </Link>
                .
              </p>
            </div>

            <div className="space-y-6 pt-2">
              <div className="flex items-start gap-4 pb-5 border-b border-taupe-border/60">
                <div className="text-espresso text-lg shrink-0 mt-0.5">
                  <i className="fa-solid fa-location-dot" aria-hidden="true" />
                </div>
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-taupe">Adresse</h4>
                  <p className="text-sm font-semibold text-espresso mt-0.5">Cryo by Breum</p>
                  <p className="text-sm text-espresso-light">Dalumvej 82, 5250 Odense SV</p>
                </div>
              </div>

              <div className="flex items-start gap-4 pb-5 border-b border-taupe-border/60">
                <div className="text-espresso text-lg shrink-0 mt-0.5">
                  <i className="fa-solid fa-phone" aria-hidden="true" />
                </div>
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-taupe">Telefon</h4>
                  <a 
                    href="tel:+4526434402" 
                    className="text-sm font-semibold text-espresso hover:text-accent-brown transition-colors whitespace-nowrap block mt-0.5"
                  >
                    26 43 44 02
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4 pb-5 border-b border-taupe-border/60">
                <div className="text-espresso text-lg shrink-0 mt-0.5">
                  <i className="fa-regular fa-clock" aria-hidden="true" />
                </div>
                <div className="w-full">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-taupe">Åbningstider</h4>
                  <div className="mt-2 space-y-1.5 text-sm">
                    <div className="flex justify-between border-b border-taupe-border/40 pb-1">
                      <span className="text-taupe">Mandag:</span>
                      <span className="font-semibold text-espresso">12:00 – 19:00</span>
                    </div>
                    <div className="flex justify-between border-b border-taupe-border/40 pb-1">
                      <span className="text-taupe">Tirsdag:</span>
                      <span className="font-semibold text-espresso">11:00 – 22:00</span>
                    </div>
                    <div className="flex justify-between border-b border-taupe-border/40 pb-1">
                      <span className="text-taupe">Onsdag:</span>
                      <span className="font-semibold text-espresso">10:00 – 20:00</span>
                    </div>
                    <div className="flex justify-between border-b border-taupe-border/40 pb-1">
                      <span className="text-taupe">Torsdag:</span>
                      <span className="font-semibold text-espresso">10:00 – 18:30</span>
                    </div>
                    <div className="flex justify-between pb-1">
                      <span className="text-taupe">Fredag:</span>
                      <span className="font-semibold text-espresso text-right">
                        10:00 – 16:00
                        <span className="block text-xs font-normal text-taupe">(i ulige uger)</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="text-espresso text-lg shrink-0 mt-0.5">
                  <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                </div>
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-taupe">Booking</h4>
                  <p className="text-sm text-espresso-light mt-0.5">
                    Åbent efter tidsbestilling via online booking.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-7 reveal-on-scroll delay-150">
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
}
